import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, Save, X } from 'lucide-react';
import { uploadMultipleImages } from '../services/cloudinary';
import { createBorrower } from '../services/api';
import Toast from '../components/Toast';
import ImageSourcePicker from '../components/ImageSourcePicker';
import { useLanguage } from '../contexts/LanguageContext';

const CreateBorrower = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const texts = t('createBorrower');
  
  // Check if redirected from CreateLoan
  const isLoanMode = location.state?.fromLoan || false;
  const [formData, setFormData] = useState({
    fullName: '',
    fatherName: '',
    email: '',
    address: '',
    phone: '',
    idProofType: 'Aadhaar Card',
    idProofNumber: ''
  });
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the limit
    if (selectedPhotos.length + files.length > 2) {
      setToast({ 
        show: true, 
        message: 'Maximum 2 photos allowed', 
        type: 'error' 
      });
      return;
    }
    
    // Validate each file
    for (const file of files) {
      // Validate file type (images only)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setToast({ 
          show: true, 
          message: 'Please select valid image files (JPEG, PNG)', 
          type: 'error' 
        });
        return;
      }
      
      // Validate file size (max 5MB per image)
      if (file.size > 5 * 1024 * 1024) {
        setToast({ 
          show: true, 
          message: `${file.name} is too large. Maximum size is 5MB per image`, 
          type: 'error' 
        });
        return;
      }
    }
    
    setSelectedPhotos(prev => [...prev, ...files]);
    setToast({ 
      show: true, 
      message: `${files.length} photo(s) selected`, 
      type: 'success' 
    });
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving || isUploading) return;
    
    // Validate email format if provided
    // Validate required fields
    if (!formData.fullName.trim()) {
      setToast({ show: true, message: 'Full name is required', type: 'error' });
      return;
    }
    if (!formData.fatherName.trim()) {
      setToast({ show: true, message: 'Father/Husband/Dependent name is required', type: 'error' });
      return;
    }
    if (!formData.phone.trim()) {
      setToast({ show: true, message: 'Phone number is required', type: 'error' });
      return;
    }
    if (!formData.address.trim()) {
      setToast({ show: true, message: 'Address is required', type: 'error' });
      return;
    }
    if (!formData.idProofNumber.trim()) {
      setToast({ show: true, message: 'ID proof number is required', type: 'error' });
      return;
    }

    const emailValue = formData.email.trim();
    if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setToast({ 
        show: true, 
        message: 'Please enter a valid email address or leave it empty', 
        type: 'error' 
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      let documentsUrl = '';
      
      // STEP 1: Handle image uploads if photos are selected
      if (selectedPhotos.length > 0) {
        setIsUploading(true);
        
        try {
          console.log('🔄 STEP 1: Starting image upload...', {
            photoCount: selectedPhotos.length
          });
          
          const uploadedUrls = await uploadMultipleImages(selectedPhotos, { folder: 'borrowers' });
          
          // Join URLs with comma
          documentsUrl = uploadedUrls.join(',');
          
          console.log('✅ STEP 1 COMPLETE: Images uploaded successfully to Cloudinary');
          console.log('📄 Document URLs:', documentsUrl);
          
          setToast({ 
            show: true, 
            message: 'Images uploaded successfully!', 
            type: 'success' 
          });
        } catch (uploadError) {
          console.error('❌ STEP 1 FAILED: Upload error:', uploadError);
          setToast({ 
            show: true, 
            message: `Image upload failed: ${uploadError.message}. Please try again.`, 
            type: 'error' 
          });
          return;
        } finally {
          setIsUploading(false);
        }
      } else {
        console.log('📝 No photos selected - proceeding without image upload');
      }

      // STEP 2: Create borrower
      // Email is optional: send null if empty, otherwise send the email value
      const payload = {
        fullName: formData.fullName.trim(),
        fatherName: formData.fatherName.trim(),
        contactNumber: formData.phone.trim(),
        email: emailValue || null, // Send null instead of empty string
        address: formData.address.trim(),
        idProofType: formData.idProofType,
        idProofNumber: formData.idProofNumber.trim(),
        documentsUrl: documentsUrl || 'string'
      };

      console.log('🔄 STEP 2: Creating borrower with payload:', payload);
      console.log('📋 Father Name value:', formData.fatherName, '| Trimmed:', formData.fatherName.trim());
      
      const result = await createBorrower(payload);
      if (result.success) {
        console.log('✅ STEP 2 COMPLETE: Borrower created successfully');
        setToast({ show: true, message: 'Borrower created successfully', type: 'success' });
        
        // If redirected from CreateLoan, go back to CreateLoan with borrower data
        if (isLoanMode && result.data?.id) {
          setTimeout(() => {
            navigate('/create-loan', { 
              state: { 
                selectedBorrowerId: result.data.id,
                selectedBorrowerName: formData.fullName 
              } 
            });
          }, 800);
        } else {
          setTimeout(() => navigate('/borrowers'), 800);
        }
      } else {
        // Handle specific error cases
        if (result.message && result.message.includes('Email is already in use')) {
          setToast({ 
            show: true, 
            message: 'This email address is already registered. Please use a different email or leave it empty.', 
            type: 'error' 
          });
        } else if (result.message && result.message.includes('Phone')) {
          setToast({ 
            show: true, 
            message: 'This phone number is already registered. Please use a different phone number.', 
            type: 'error' 
          });
        } else {
          setToast({ 
            show: true, 
            message: result.message || 'Failed to create borrower. Please check your information and try again.', 
            type: 'error' 
          });
        }
        throw new Error(result.message || 'Failed to create borrower');
      }
    } catch (err) {
      console.error('Error in process:', err);
      setToast({ show: true, message: err.message || 'Error saving borrower', type: 'error' });
    } finally {
      setIsSaving(false);
      setIsUploading(false);
    }
  };

  const idProofTypes = [
    'Aadhaar Card',
    'PAN Card',
    'Driving License',
    'Voter ID',
    'Passport'
  ];

  return (
    <div>
      <div className="card">
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 className="card-title">{texts.title || 'Create New Borrower Profile'}</h2>
          {isLoanMode && (
            <div className="create-borrower-loan-hint">
              ℹ️ After creating this borrower, you'll be redirected back to create the loan with this borrower selected.
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* First Row - 3 columns */}
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
            <div className="form-group">
              <label className="form-label">{texts.fullName || 'Full Name'} *</label>
              <input
                type="text"
                name="fullName"
                className="form-input"
                placeholder={texts.fullNamePlaceholder || "Enter full name"}
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{texts.fatherHusbandName || 'Father/Husband/Dependent Name'} *</label>
              <input
                type="text"
                name="fatherName"
                className="form-input"
                placeholder={texts.fatherHusbandNamePlaceholder || "Enter father/husband/dependent name"}
                value={formData.fatherName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{texts.phoneNumber || 'Phone Number'} *</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                placeholder={texts.phoneNumberPlaceholder || "Enter phone number"}
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Second Row - 3 columns */}
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
            <div className="form-group">
              <label className="form-label">{texts.idProofType || 'ID Proof Type'} *</label>
              <select
                name="idProofType"
                className="form-select"
                value={formData.idProofType}
                onChange={handleInputChange}
                required
              >
                {idProofTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{texts.idProofNumber || 'ID Proof Number'} *</label>
              <input
                type="text"
                name="idProofNumber"
                className="form-input"
                placeholder={texts.idProofNumberPlaceholder || "Enter ID number"}
                value={formData.idProofNumber}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{texts.email || 'Email Address (Optional)'}</label>
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder={texts.emailPlaceholder || "Enter email address"}
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Third Row - 2 columns */}
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">{texts.fullAddress || 'Full Address'} *</label>
              <textarea
                name="address"
                className="form-textarea"
                placeholder={texts.fullAddressPlaceholder || "Enter full address"}
                value={formData.address}
                onChange={handleInputChange}
                rows={5}
                style={{ minHeight: '130px', maxHeight: '130px' }}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">{texts.uploadIdProof || 'Upload ID Proof Photos (Max 2)'}</label>
              <div
                className={`id-proof-upload${selectedPhotos.length > 0 ? ' has-files' : ''}`}
                style={{ opacity: selectedPhotos.length >= 2 ? 0.6 : 1 }}
              >
                <Upload size={24} className="id-proof-upload-icon" />
                {selectedPhotos.length > 0 ? (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div className="id-proof-upload-success">
                      ✓ {selectedPhotos.length} photo(s) selected
                    </div>
                    <div className="id-proof-upload-hint">
                      {selectedPhotos.length < 2 ? 'Add more from gallery or camera' : 'Maximum reached'}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div className="id-proof-upload-muted">
                      {texts.clickToUpload || 'Upload photos'}
                    </div>
                    <div className="id-proof-upload-sub">
                      {texts.supportedFormats || 'Supports: JPEG, PNG (Max 5MB each)'}
                    </div>
                  </div>
                )}
                {selectedPhotos.length < 2 && (
                  <ImageSourcePicker
                    multiple
                    disabled={isUploading || selectedPhotos.length >= 2}
                    onChange={handlePhotoChange}
                    galleryLabel={t('common.gallery')}
                    cameraLabel={t('common.camera')}
                  />
                )}
              </div>

              {/* Display selected photos */}
              {selectedPhotos.length > 0 && (
                <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                  {selectedPhotos.map((photo, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`ID Proof ${index + 1}`}
                        className="id-proof-photo"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="id-proof-photo-remove"
                        title="Remove photo"
                      >
                        <X size={14} />
                      </button>
                      <div className="id-proof-photo-size">
                        {(photo.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
            <button type="submit" className="btn btn-primary" disabled={isSaving || isUploading}>
              <Save size={16} />
              {isUploading ? 'Uploading...' : isSaving ? (texts.saving || 'Saving...') : (texts.saveBorrower || 'Save Borrower')}
            </button>
          </div>
        </form>
      </div>
      {toast.show && (
        <Toast 
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}
    </div>
  );
};

export default CreateBorrower;
